import React, { useEffect } from "react";
import {
  X,
  Clock,
  Calendar,
  Code,
  Check,
  Loader,
  FileText,
  Shield,
} from "lucide-react";
import { normalizePermissionFeatureName } from "../../../utils/permissionNormalization";
import { getWorkflowApprovalLabel } from "../Constant/EmployeeActions.config";

const PendingApprovalsModal = ({
  t,
  styles,
  theme,
  isRTL,
  language,
  pendingActions,
  pendingWorkflows,
  isSubmitting,
  permissions,
  isAdminView,
  isAdminRole,
  ACTION_TYPES,
  profile,
  workflowDefinitions,
  highlightActionId = null,
  onApprove,
  onReject,
  onClose,
}) => {
  // Notification deep-link: scroll the highlighted change into view.
  useEffect(() => {
    if (!highlightActionId) return;
    const timer = setTimeout(() => {
      document.getElementById(`pending-action-${highlightActionId}`)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 150);
    return () => clearTimeout(timer);
  }, [highlightActionId]);

  return (
    <div style={styles.modal} onClick={onClose}>
      <div
        style={{ ...styles.modalContent, maxWidth: "min(95vw, 1200px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={styles.modalHeader}>
          <div
            style={{
              display: "flex",
              flexDirection: isRTL ? "row" : "row",
              alignItems: "center",
            }}
          >
            <div
              style={{
                ...styles.modalIconWrapper,
                background: "linear-gradient(135deg, #ec4899, #db2777)",
              }}
            >
              <Clock size={26} color="white" />
            </div>
            <div>
              <h2 style={styles.modalTitle}>{t.pendingApprovalsTitle}</h2>
              <p
                style={{
                  fontSize: "clamp(12px, 1.7vw, 14px)",
                  color:
                    theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
                  margin: "4px 0 0 0",
                  fontWeight: "600",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {pendingActions.length} {t.actionsAwaiting}
              </p>
            </div>
          </div>
          <button
            style={styles.closeButton}
            onClick={onClose}
            className="enhanced-button-hover"
          >
            <X size={24} />
          </button>
        </div>

        {isSubmitting && (
          <div style={styles.loadingOverlay}>
            <div style={{ textAlign: "center" }}>
              <Loader size={48} style={styles.loadingSpinner} color="#2e7cba" />
              <p
                style={{
                  marginTop: "16px",
                  fontWeight: "600",
                  color:
                    theme === "dark" ? "rgba(255,255,255,0.8)" : "#64748b",
                }}
              >
                {t.processing}
              </p>
            </div>
          </div>
        )}

        <div style={styles.tableWrapper}>
          {pendingActions.length > 0 ? (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>{t.requestedDate}</th>
                  <th style={styles.th}>{t.employee}</th>
                  <th style={styles.th}>{t.actionType}</th>
                  <th style={styles.th}>{t.effectiveDate}</th>
                  <th style={styles.th}>{t.details}</th>
                  <th style={styles.th}>{t.approvalLevel}</th>
                  <th style={styles.th}>{t.actions}</th>
                </tr>
              </thead>
              <tbody>
                {pendingActions.map((action) => {
                  const actionConfig = ACTION_TYPES.find(
                    (a) => a.id === action.actionType
                  );
                  const ActionIcon = actionConfig?.icon || FileText;

                  const isHighlighted = Number(highlightActionId) === Number(action.id);

                  return (
                    <tr
                      key={action.id}
                      id={`pending-action-${action.id}`}
                      className="table-row-hover"
                      style={isHighlighted ? { outline: "2px solid #3b82f6", outlineOffset: "-2px" } : undefined}
                    >
                      <td style={styles.td}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: isRTL ? "row" : "row",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Calendar size={16} />
                          {new Date(
                            action.createdAt || action.requestedDate
                          ).toLocaleDateString(
                            language === "ar" ? "ar-EG" : "en-US"
                          )}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.employeeInfo}>
                          <div style={styles.employeeName}>
                            {language === "ar"
                              ? action.employeeArabicName ||
                                action.employeeName ||
                                action.newData?.employeeName ||
                                t.notAvailable
                              : action.employeeName ||
                                action.employeeArabicName ||
                                action.newData?.employeeName ||
                                t.notAvailable}
                          </div>
                          <div style={styles.employeeDetail}>
                            <Code size={13} />
                            {action.infinityCode ||
                              action.employeeId ||
                              t.notAvailable}
                          </div>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div
                          style={{
                            ...styles.badge,
                            background:
                              actionConfig?.bgColor ||
                              "rgba(100, 116, 139, 0.15)",
                            border: `1px solid ${
                              actionConfig?.borderColor ||
                              "rgba(100, 116, 139, 0.4)"
                            }`,
                            color: theme === "dark" ? "#ffffff" : "#0f172a",
                          }}
                        >
                          <ActionIcon size={14} />
                          {actionConfig
                            ? t[actionConfig.labelKey]
                            : action.actionType}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: isRTL ? "row" : "row",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Calendar size={16} />
                          {action.effectiveDate
                            ? new Date(
                                action.effectiveDate
                              ).toLocaleDateString(
                                language === "ar" ? "ar-EG" : "en-US"
                              )
                            : t.notAvailable}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div
                          style={{
                            fontSize: "clamp(11px, 1.6vw, 13px)",
                            color:
                              theme === "dark"
                                ? "rgba(255,255,255,0.65)"
                                : "#64748b",
                            maxWidth: "250px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            textAlign: isRTL ? "right" : "left",
                          }}
                          title={action.reason}
                        >
                          {action.reason || t.noDetailsProvided}
                        </div>
                        {action.oldData && action.newData && (
                          <div
                            style={{
                              fontSize: "clamp(10px, 1.5vw, 11px)",
                              color:
                                theme === "dark"
                                  ? "rgba(255,255,255,0.5)"
                                  : "#94a3b8",
                              marginTop: "4px",
                              textAlign: isRTL ? "right" : "left",
                            }}
                          >
                            {action.oldData.position &&
                              action.newData.position &&
                              `${action.oldData.position} ${
                                isRTL ? "←" : "→"
                              } ${action.newData.position}`}
                            {action.oldData.salary &&
                              action.newData.salary &&
                              ` | ${t.egp} ${action.oldData.salary?.toLocaleString()} ${
                                isRTL ? "←" : "→"
                              } ${action.newData.salary?.toLocaleString()}`}
                          </div>
                        )}
                      </td>
                      <td style={{ ...styles.td, whiteSpace: "normal" }}>
                        <div
                          style={{
                            ...styles.badge,
                            background: "rgba(139, 92, 246, 0.15)",
                            border: "1px solid rgba(139, 92, 246, 0.4)",
                            color:
                              theme === "dark" ? "#c4b5fd" : "#7c3aed",
                            // This column holds the full workflow approval
                            // chain (can be several steps long) — cap it so
                            // it wraps within the column instead of forcing
                            // the whole table wider.
                            maxWidth: "260px",
                          }}
                        >
                          <Shield size={14} style={{ flexShrink: 0 }} />
                          {actionConfig
                            ? getWorkflowApprovalLabel(
                                actionConfig.id,
                                workflowDefinitions,
                                language,
                                t,
                                actionConfig.approvalLevelKey
                              )
                            : t.notAvailable}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: isRTL ? "row" : "row",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          {(() => {
                            const normalizeApproverValue = (value) =>
                              String(value || "").trim().toLowerCase();

                            const currentUserCandidates = [
                              profile?.infinityCode,
                              profile?.employeeId,
                              profile?.userId,
                              profile?.code,
                              localStorage.getItem("infinityCode"),
                              localStorage.getItem("userInfinityCode"),
                              localStorage.getItem("employeeCode"),
                              sessionStorage.getItem("infinityCode"),
                            ].filter(Boolean);
                            const currentUserNormalized = currentUserCandidates.map(normalizeApproverValue);
                            const currentAdminLevelIds = [
                              profile?.adminLevelId,
                              profile?.adminLevel?.id,
                              profile?.admin_level_id,
                              (() => {
                                try {
                                  const userStr =
                                    localStorage.getItem("user") ||
                                    sessionStorage.getItem("user");
                                  const obj = userStr ? JSON.parse(userStr) : null;
                                  return obj?.adminLevelId || obj?.admin_level_id || obj?.adminLevel?.id || null;
                                } catch {
                                  return null;
                                }
                              })(),
                            ].filter(Boolean).map((value) => String(value).trim());

                            const wf =
                              pendingWorkflows?.[String(action.id)] ||
                              pendingWorkflows?.[action.id] ||
                              pendingWorkflows?.[String(action.workflowInstanceId)] ||
                              pendingWorkflows?.[action.workflowInstanceId] ||
                              [];

                            const employeeManagerCodes = [
                              action?.employee?.firstLineManagerInfinityCode,
                              action?.employee?.secondLineManagerInfinityCode,
                              action?.employee?.firstLineManager?.infinityCode,
                              action?.employee?.secondLineManager?.infinityCode,
                              action?.employee?.managerInfinityCode,
                              action?.employee?.directManager,
                              action?.employee?.lineManager,
                            ].filter(Boolean).map(normalizeApproverValue);

                            const isApprover = Array.isArray(wf)
                              ? wf.some((step) => {
                                  const stepStatus = String(step?.status || "").trim().toLowerCase();
                                  if (stepStatus !== "pending") return false;

                                  const assignedCodes = [
                                    step?.assignedToInfinityCode,
                                    step?.explicitAssigneeInfinityCode,
                                    step?.assignedTo,
                                    step?.assignedToId,
                                    step?.assignedToInfinity,
                                    step?.assigneeInfinityCode,
                                    step?.assignee?.infinityCode,
                                    step?.assignee?.code,
                                  ].filter(Boolean).map(normalizeApproverValue);

                                  const isUserAssignee = assignedCodes.some((code) =>
                                    currentUserNormalized.includes(code)
                                  );
                                  const isAdminAssignee = Boolean(
                                    currentAdminLevelIds.length &&
                                      step?.assignedToAdminLevelId &&
                                      currentAdminLevelIds.includes(String(step.assignedToAdminLevelId).trim())
                                  );
                                  const isEmployeeManager = employeeManagerCodes.some((code) =>
                                    currentUserNormalized.includes(code)
                                  );

                                  return isUserAssignee || isAdminAssignee || isEmployeeManager;
                                })
                              : false;

                            if (!isApprover) return null;

                            // Registry keys (case-insensitive match):
                            // "approveactions" / "rejectactions"
                            const grantedFeatures = Array.isArray(
                              permissions?.features?.changesPage
                            )
                              ? permissions.features.changesPage.map((f) =>
                                  String(f || "").trim().toLowerCase()
                                )
                              : [];
                            const canApprove =
                              !isAdminView ||
                              isAdminRole ||
                              grantedFeatures.includes("approveactions");
                            const canReject =
                              !isAdminView ||
                              isAdminRole ||
                              grantedFeatures.includes("rejectactions");

                            return (
                              <>
                                {canApprove && (
                                  <button
                                    style={{
                                      ...styles.actionButton,
                                      background:
                                        "linear-gradient(135deg, #10b981, #059669)",
                                      padding:
                                        "clamp(8px, 1.3vw, 10px) clamp(14px, 2vw, 16px)",
                                      fontSize: "clamp(11px, 1.6vw, 12px)",
                                    }}
                                    onClick={() => onApprove(action)}
                                    className="action-btn-hover"
                                    disabled={isSubmitting}
                                  >
                                    <Check size={14} /> {t.approve}
                                  </button>
                                )}
                                {canReject && (
                                  <button
                                    style={{
                                      ...styles.actionButton,
                                      background:
                                        "linear-gradient(135deg, #ef4444, #dc2626)",
                                      padding:
                                        "clamp(8px, 1.3vw, 10px) clamp(14px, 2vw, 16px)",
                                      fontSize: "clamp(11px, 1.6vw, 12px)",
                                    }}
                                    onClick={() => onReject(action)}
                                    className="action-btn-hover"
                                    disabled={isSubmitting}
                                  >
                                    <X size={14} /> {t.reject}
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyStateIcon}>
                <Clock size={80} />
              </div>
              <h3 style={styles.emptyStateTitle}>{t.noPendingApprovals}</h3>
              <p style={styles.emptyStateText}>{t.allActionsReviewed}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PendingApprovalsModal;
